import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Combobox from "@/components/Combobox";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { formatCurrency, formatNumber } from "@/lib/format";
import { Loader2, Calculator } from "lucide-react";

const empty = { data_viaggio: new Date().toISOString().slice(0, 10), localita_partenza_id: "", localita_arrivo_id: "", prodotto_id: "", km: "", quantita_litri: "", quantita_kg: "", quantita_ton: "", quantita_pezzi: "", ore: "", numero_scarichi: "", supplementi: "" };

export default function SimulatoreDialog({ open, onOpenChange, listini }) {
  const [listinoId, setListinoId] = useState("");
  const [v, setV] = useState(empty);
  const [opts, setOpts] = useState({});
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRes(null);
    Promise.all([api.get("/localita/options"), api.get("/prodotti/options")]).then(([l, p]) => setOpts({ localita: l.data, prodotti: p.data }));
  }, [open]);

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const run = async () => {
    if (!listinoId) {
      toast.error("Seleziona un listino");
      return;
    }
    setBusy(true);
    try {
      const viaggio = { ...v, supplementi: v.supplementi.split(/[,;]/).map((s) => s.trim().toUpperCase()).filter(Boolean) };
      ["km", "quantita_litri", "quantita_kg", "quantita_ton", "quantita_pezzi", "ore", "numero_scarichi"].forEach((k) => (viaggio[k] = viaggio[k] === "" ? null : Number(viaggio[k])));
      const { data } = await api.post("/listini/simula", { listino_id: listinoId, viaggio });
      setRes(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const num = (k, label) => <div><Label className="text-xs">{label}</Label><Input type="number" step="any" className="mt-1 h-9" value={v[k]} onChange={(e) => set(k, e.target.value)} data-testid={`sim-${k}`} /></div>;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="simulatore-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Simulatore tariffa</DialogTitle>
          <DialogDescription>Calcola l'importo di un viaggio ipotetico su un listino, con supplementi e gasolio.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2"><Label className="text-xs">Listino</Label><div className="mt-1"><Combobox options={listini.map((l) => ({ value: l.id, label: `${l.nome} — ${l.soggetto_nome}` }))} value={listinoId} onChange={setListinoId} placeholder="Seleziona listino..." testid="sim-listino" /></div></div>
          <div><Label className="text-xs">Data viaggio</Label><Input type="date" className="mt-1 h-9" value={v.data_viaggio} onChange={(e) => set("data_viaggio", e.target.value)} data-testid="sim-data" /></div>
          <div><Label className="text-xs">Partenza</Label><div className="mt-1"><Combobox options={opts.localita || []} value={v.localita_partenza_id} onChange={(x) => set("localita_partenza_id", x)} placeholder="—" testid="sim-partenza" /></div></div>
          <div><Label className="text-xs">Arrivo</Label><div className="mt-1"><Combobox options={opts.localita || []} value={v.localita_arrivo_id} onChange={(x) => set("localita_arrivo_id", x)} placeholder="—" testid="sim-arrivo" /></div></div>
          <div><Label className="text-xs">Prodotto</Label><div className="mt-1"><Combobox options={opts.prodotti || []} value={v.prodotto_id} onChange={(x) => set("prodotto_id", x)} placeholder="—" testid="sim-prodotto" /></div></div>
          {num("km", "Km")}{num("quantita_litri", "Litri")}{num("quantita_kg", "Kg")}{num("quantita_ton", "Tonnellate")}{num("quantita_pezzi", "Pezzi")}{num("ore", "Ore")}{num("numero_scarichi", "Nr. scarichi")}
          <div className="sm:col-span-2"><Label className="text-xs">Supplementi (codici, separati da virgola)</Label><Input className="mt-1 h-9 uppercase" value={v.supplementi} onChange={(e) => set("supplementi", e.target.value)} placeholder="SABATO, PERNOTTO" data-testid="sim-supplementi" /></div>
        </div>
        <div className="flex justify-end"><Button onClick={run} disabled={busy} data-testid="sim-run">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Calculator className="h-4 w-4 mr-1.5" />}Calcola</Button></div>
        {res && (
          <div className="border border-slate-200 rounded-md p-4 bg-slate-50 text-sm space-y-1" data-testid="sim-result">
            {res.warning && <div className="text-amber-700 text-xs mb-2" data-testid="sim-warning">⚠ {res.warning}</div>}
            {res.metodo && <div className="flex justify-between"><span className="text-slate-600">Tariffa "{res.descrizione_tariffa || res.metodo}": {formatNumber(res.quantita, 2)} × {formatNumber(res.prezzo_unitario, 4)}{res.minimo_applicato ? " (minimo applicato)" : ""}</span><span className="font-medium">{formatCurrency(res.importo_base)}</span></div>}
            {res.supplementi?.map((s) => <div key={s.codice} className="flex justify-between"><span className="text-slate-600">Supplemento {s.codice}</span><span>{formatCurrency(s.importo)}</span></div>)}
            {res.gasolio_pct ? <div className="flex justify-between"><span className="text-slate-600">Gasolio {formatNumber(res.gasolio_pct, 2)}%</span><span>{formatCurrency(res.gasolio_importo)}</span></div> : null}
            <div className="flex justify-between border-t border-slate-300 pt-2 mt-2 text-base font-head font-bold"><span>Totale viaggio</span><span data-testid="sim-totale">{formatCurrency(res.importo)}</span></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
